import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { FileExtension } from '@dealstack/shared/lib/common/dataExchange/common.ts';
import {
	DocumentWorkflowTemplateSourceFormat,
	type DocumentWorkflowTemplateInfo,
} from '@dealstack/shared/lib/generated';
import { documentWorkflowMasterDocumentsControllerRenderDocxTemplateFieldsViewOptions } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import { getFileReactQuery } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { useDownloadedFileUrl } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadedFileUrl.ts';

interface Props {
	document: DocumentWorkflowTemplateInfo | undefined;
	dealId: string;
	programmeId: string;
	organizationId?: string;
}

/**
 * What to show when previewing a master document.
 *
 * A PDF master document IS its uploaded file, so that file is what gets previewed. A Word one is
 * not: the upload is converted to a reflow document at create, every edit is saved against that
 * model, and generation renders it — the .docx on S3 is never rewritten. Previewing it would
 * show the author their original tokens as literal `{{ }}` text and none of the fields they
 * placed since. So a Word document is rendered through the same renderer generation uses,
 * fields shown by name, which is what the binding screen shows.
 */
export const useMasterDocumentPreviewFile = ({
	document,
	dealId,
	programmeId,
	organizationId,
}: Props): {
	file: Blob | string | undefined;
	fileName: string;
	fileExtension: string | undefined;
	isLoading: boolean;
} => {
	const templateId = document?.id ?? '';
	const isDocx = document?.sourceFormat === DocumentWorkflowTemplateSourceFormat.DOCX;

	const { downloadUrl, isLoading: isLoadingUpload } = useDownloadedFileUrl({
		fileId: isDocx ? '' : document?.file?.id ?? '',
		dealId,
		organizationId,
	});

	// Two queries rather than one chained fetch: the rendered file is a fresh PDF per render, so
	// keying the download on its id keeps the render from re-running when only the download is
	// retried.
	const { data: render, isLoading: isRendering } = useQuery({
		...documentWorkflowMasterDocumentsControllerRenderDocxTemplateFieldsViewOptions({
			path: { dealId, programmeId, templateId },
		}),
		enabled: isDocx && !!templateId && !!dealId && !!programmeId,
	});

	const renderedFileId = render?.fileId;
	const { data: renderedFile, isLoading: isLoadingRenderedFile } = useQuery({
		queryKey: [API_PATHS.FILE_ID_GET, { id: renderedFileId }, 'master-document-preview'],
		queryFn: async ({ signal }) => {
			if (!renderedFileId) {
				return undefined;
			}
			const response = await getFileReactQuery({ id: renderedFileId }, signal);
			return response ? await response.blob() : undefined;
		},
		enabled: !!renderedFileId,
	});

	if (isDocx) {
		// A rendered PDF, so it is described as one. Reporting the source `docx` here would send
		// a Blob back down the viewer's Office branch, which is where this started.
		return {
			file: renderedFile,
			fileName: document?.name ?? '',
			fileExtension: FileExtension.pdf,
			isLoading: isRendering || isLoadingRenderedFile,
		};
	}

	return {
		file: downloadUrl,
		fileName: document?.file?.name ?? '',
		fileExtension: document?.file?.extension,
		isLoading: isLoadingUpload,
	};
};
